import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  Tooltip,
  Badge,
  Slider,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Assignment as WorkOrderIcon,
  Business as ContractorIcon,
  LocationOn as LocationIcon,
  Schedule as TimelineIcon,
  TrendingUp as ProgressIcon,
  CheckCircle as SubmitIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Visibility as ViewProgressIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useAuth } from "../contexts/AuthContext";
import { workOrderDetailsAPI, contractorWorkOrderAPI } from "../services/api";
import WorkOrderForm from "../components/WorkOrders/WorkOrderForm";

const WorkOrders = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openProgressDialog, setOpenProgressDialog] = useState(false);
  const [openSubmitDialog, setOpenSubmitDialog] = useState(false);
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [openRejectionDialog, setOpenRejectionDialog] = useState(false);
  const [openProgressViewDialog, setOpenProgressViewDialog] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [progressValue, setProgressValue] = useState(0);
  const [progressComments, setProgressComments] = useState("");
  const [submitComments, setSubmitComments] = useState("");
  const [approvalComments, setApprovalComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [localProgressUpdates, setLocalProgressUpdates] = useState({});

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "approved", label: "Approved" },
    { value: "completed", label: "Completed" },
    { value: "pending_approval", label: "Pending Approval" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const priorityOptions = [
    { value: "all", label: "All Priority" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  const tabs = [
    { label: "All Work Orders", value: 0 },
    { label: "Active", value: 1 },
    { label: "Pending Approval", value: 2 },
    { label: "Completed", value: 3 },
    { label: "Cancelled", value: 4 },
  ];

  const loadWorkOrders = useCallback(async () => {
    try {
      setLoading(true);

      // Load progress updates from localStorage
      const storedUpdates = JSON.parse(
        localStorage.getItem("workOrderProgressUpdates") || "{}"
      );
      setLocalProgressUpdates(storedUpdates);

      // Use different API based on user role
      const isContractor =
        user?.role === "contractor_user" || user?.role === "contractor_admin";
      const apiCall = isContractor
        ? contractorWorkOrderAPI.getAssignedWorkOrders
        : workOrderDetailsAPI.getAll;

      const response = await apiCall({
        page: 1,
        limit: 100,
        status: filterStatus === "all" ? undefined : filterStatus,
        priority: filterPriority === "all" ? undefined : filterPriority,
        search: searchTerm || undefined,
      });

      if (response.data.success) {
        const serverWorkOrders = response.data.workOrders || [];
        console.log("Server work orders:", serverWorkOrders);
        console.log(
          "First work order contractor data:",
          serverWorkOrders[0]?.contractorAssignment
        );

        // Merge local progress updates with server data
        const mergedWorkOrders = serverWorkOrders.map((wo) => {
          const localUpdate = storedUpdates[wo._id];
          if (localUpdate) {
            return {
              ...wo,
              statusTracking: {
                ...wo.statusTracking,
                progressPercentage: localUpdate.progressPercentage,
                currentStatus: localUpdate.currentStatus,
              },
            };
          }
          return wo;
        });

        setWorkOrders(mergedWorkOrders);
      } else {
        setWorkOrders([]);
      }
    } catch (error) {
      enqueueSnackbar("Failed to load work orders", { variant: "error" });
      console.error("Error loading work orders:", error);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, user?.role, filterStatus, filterPriority, searchTerm]);

  useEffect(() => {
    loadWorkOrders();
  }, [loadWorkOrders]);

  const handleCreateWorkOrder = () => {
    setSelectedWorkOrder(null);
    setOpenForm(true);
  };

  const handleEditWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setOpenForm(true);
  };

  const handleViewWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setOpenViewDialog(true);
  };

  const handleViewProgress = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setOpenProgressViewDialog(true);
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    if (window.confirm("Are you sure you want to cancel this work order?")) {
      try {
        // Update status to cancelled instead of deleting
        await workOrderDetailsAPI.updateStatus(workOrderId, {
          status: "cancelled",
          reason: "Work order cancelled by admin",
        });
        enqueueSnackbar("Work order cancelled successfully", {
          variant: "success",
        });
        
        // Update the work order status to "cancelled" locally
        const updatedWorkOrder = {
          ...workOrders.find(wo => wo._id === workOrderId),
          statusTracking: {
            ...workOrders.find(wo => wo._id === workOrderId)?.statusTracking,
            currentStatus: "cancelled",
          },
        };
        
        // Update local state immediately
        setWorkOrders((prevWorkOrders) =>
          prevWorkOrders.map((wo) =>
            wo._id === workOrderId ? updatedWorkOrder : wo
          )
        );
        
        // Update localStorage
        const existingUpdates = JSON.parse(
          localStorage.getItem("workOrderProgressUpdates") || "{}"
        );
        existingUpdates[workOrderId] = {
          ...existingUpdates[workOrderId],
          currentStatus: "cancelled",
        };
        localStorage.setItem(
          "workOrderProgressUpdates",
          JSON.stringify(existingUpdates)
        );
        
        // Don't call loadWorkOrders() immediately as it overrides our local state update
      } catch (error) {
        enqueueSnackbar("Failed to cancel work order", { variant: "error" });
      }
    }
  };

  // Contractor-specific handlers
  const handleUpdateProgress = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setProgressValue(
      workOrder.statusTracking?.progressPercentage ||
        workOrder.statusTracking?.overallProgress ||
        0
    );
    setProgressComments("");
    setOpenProgressDialog(true);
  };

  const handleSubmitProgress = async () => {
    try {
      const isContractor =
        user?.role === "contractor_user" || user?.role === "contractor_admin";
      if (isContractor) {
        // Determine the correct status based on current status and progress
        let newStatus;
        if (progressValue === 100) {
          newStatus = "completed";
        } else if (selectedWorkOrder.statusTracking?.currentStatus === "completed") {
          // If work order was completed and progress is updated, it should go to pending approval
          newStatus = "pending_approval";
        } else {
          newStatus = "active";
        }

        await contractorWorkOrderAPI.updateProgress(selectedWorkOrder._id, {
          progressPercentage: progressValue,
          status: newStatus,
          comments: progressComments,
        });

        enqueueSnackbar("Progress updated successfully", {
          variant: "success",
        });

        // Store the progress update locally and in localStorage
        const progressUpdate = {
          progressPercentage: progressValue,
          currentStatus: newStatus,
          updatedAt: new Date().toISOString(),
        };

        // Update local state
        setLocalProgressUpdates((prev) => ({
          ...prev,
          [selectedWorkOrder._id]: progressUpdate,
        }));

        // Also store in localStorage for persistence
        const existingUpdates = JSON.parse(
          localStorage.getItem("workOrderProgressUpdates") || "{}"
        );
        existingUpdates[selectedWorkOrder._id] = progressUpdate;
        localStorage.setItem(
          "workOrderProgressUpdates",
          JSON.stringify(existingUpdates)
        );

        // Update the work order in the local state immediately
        const updatedWorkOrder = {
          ...selectedWorkOrder,
          statusTracking: {
            ...selectedWorkOrder.statusTracking,
            progressPercentage: progressValue,
            currentStatus: newStatus,
          },
        };

        setWorkOrders((prevWorkOrders) =>
          prevWorkOrders.map((wo) =>
            wo._id === selectedWorkOrder._id ? updatedWorkOrder : wo
          )
        );

        // Also update the selectedWorkOrder if it's the same work order
        setSelectedWorkOrder(updatedWorkOrder);

        // Close dialog
        setOpenProgressDialog(false);

        // Don't call loadWorkOrders() immediately as it overrides our local state update
        // The local state update will persist the progress change in the UI
      } else {
        setOpenProgressDialog(false);
      }
    } catch (error) {
      console.error("Error updating progress:", error);
      enqueueSnackbar("Failed to update progress", { variant: "error" });
    }
  };

  const handleSubmitForApproval = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setSubmitComments("");
    setOpenSubmitDialog(true);
  };

  const handleSubmitApproval = async () => {
    try {
      console.log("Current user role:", user?.role);
      const isContractor =
        user?.role === "contractor_user" || user?.role === "contractor_admin";
      console.log("Is contractor:", isContractor);
      if (isContractor) {
        await contractorWorkOrderAPI.submitForApproval(selectedWorkOrder._id, {
          comments: submitComments,
          deliverables: [],
        });
        enqueueSnackbar("Work order submitted for approval", {
          variant: "success",
        });

        // Update the work order status to "pending_approval" locally
        const updatedWorkOrder = {
          ...selectedWorkOrder,
          statusTracking: {
            ...selectedWorkOrder.statusTracking,
            currentStatus: "pending_approval",
          },
        };

        // Update local state immediately
        setWorkOrders((prevWorkOrders) =>
          prevWorkOrders.map((wo) =>
            wo._id === selectedWorkOrder._id ? updatedWorkOrder : wo
          )
        );

        // Update selectedWorkOrder
        setSelectedWorkOrder(updatedWorkOrder);

        // Update localStorage
        const existingUpdates = JSON.parse(
          localStorage.getItem("workOrderProgressUpdates") || "{}"
        );
        existingUpdates[selectedWorkOrder._id] = {
          ...existingUpdates[selectedWorkOrder._id],
          currentStatus: "pending_approval",
        };
        localStorage.setItem(
          "workOrderProgressUpdates",
          JSON.stringify(existingUpdates)
        );
      }
      setOpenSubmitDialog(false);
      // Don't call loadWorkOrders() immediately as it overrides our local state update
    } catch (error) {
      console.error("Submit for approval error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to submit for approval";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  // Tenant admin handlers
  const handleApproveWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setApprovalComments("");
    setOpenApprovalDialog(true);
  };

  const handleRejectWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setRejectionReason("");
    setOpenRejectionDialog(true);
  };

  const handleApproveSubmission = async () => {
    try {
      const isAdmin = user?.role === "admin" || user?.role === "superadmin";
      if (isAdmin) {
        await workOrderDetailsAPI.updateStatus(selectedWorkOrder._id, {
          status: "approved",
          reason: approvalComments,
        });
        enqueueSnackbar("Work order approved successfully", {
          variant: "success",
        });
        
        // Update the work order status to "approved" locally
        const updatedWorkOrder = {
          ...selectedWorkOrder,
          statusTracking: {
            ...selectedWorkOrder.statusTracking,
            currentStatus: "approved",
          },
        };
        
        // Update local state immediately
        setWorkOrders((prevWorkOrders) =>
          prevWorkOrders.map((wo) =>
            wo._id === selectedWorkOrder._id ? updatedWorkOrder : wo
          )
        );
        
        // Update selectedWorkOrder
        setSelectedWorkOrder(updatedWorkOrder);
        
        // Update localStorage
        const existingUpdates = JSON.parse(
          localStorage.getItem("workOrderProgressUpdates") || "{}"
        );
        existingUpdates[selectedWorkOrder._id] = {
          ...existingUpdates[selectedWorkOrder._id],
          currentStatus: "approved",
        };
        localStorage.setItem(
          "workOrderProgressUpdates",
          JSON.stringify(existingUpdates)
        );
      }
      setOpenApprovalDialog(false);
      // Don't call loadWorkOrders() immediately as it overrides our local state update
    } catch (error) {
      enqueueSnackbar("Failed to approve work order", { variant: "error" });
    }
  };

  const handleSubmitRejection = async () => {
    try {
      const isAdmin = user?.role === "admin" || user?.role === "superadmin";
      if (isAdmin) {
        await workOrderDetailsAPI.updateStatus(selectedWorkOrder._id, {
          status: "rejected",
          reason: rejectionReason,
        });
        enqueueSnackbar("Work order rejected", {
          variant: "warning",
        });
        
        // Update the work order status to "rejected" locally
        const updatedWorkOrder = {
          ...selectedWorkOrder,
          statusTracking: {
            ...selectedWorkOrder.statusTracking,
            currentStatus: "rejected",
          },
        };
        
        // Update local state immediately
        setWorkOrders((prevWorkOrders) =>
          prevWorkOrders.map((wo) =>
            wo._id === selectedWorkOrder._id ? updatedWorkOrder : wo
          )
        );
        
        // Update selectedWorkOrder
        setSelectedWorkOrder(updatedWorkOrder);
        
        // Update localStorage
        const existingUpdates = JSON.parse(
          localStorage.getItem("workOrderProgressUpdates") || "{}"
        );
        existingUpdates[selectedWorkOrder._id] = {
          ...existingUpdates[selectedWorkOrder._id],
          currentStatus: "rejected",
        };
        localStorage.setItem(
          "workOrderProgressUpdates",
          JSON.stringify(existingUpdates)
        );
      }
      setOpenRejectionDialog(false);
      // Don't call loadWorkOrders() immediately as it overrides our local state update
    } catch (error) {
      enqueueSnackbar("Failed to reject work order", { variant: "error" });
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedWorkOrder(null);
  };

  const handleFormSave = () => {
    setOpenForm(false);
    setSelectedWorkOrder(null);
    loadWorkOrders();
    enqueueSnackbar("Work order saved successfully", { variant: "success" });
  };

  const getFilteredWorkOrders = () => {
    let filtered = workOrders;

    // Tab filtering
    switch (tabValue) {
      case 1: // Active
        filtered = filtered.filter(
          (wo) => wo.statusTracking?.currentStatus === "active"
        );
        break;
      case 2: // Pending Approval
        filtered = filtered.filter(
          (wo) =>
            wo.statusTracking?.currentStatus === "pending_approval" ||
            wo.statusTracking?.currentStatus === "submitted"
        );
        break;
      case 3: // Completed
        filtered = filtered.filter(
          (wo) =>
            wo.statusTracking?.currentStatus === "approved" ||
            wo.statusTracking?.currentStatus === "completed"
        );
        break;
      case 4: // Cancelled
        filtered = filtered.filter(
          (wo) =>
            wo.statusTracking?.currentStatus === "cancelled" ||
            wo.statusTracking?.currentStatus === "terminated"
        );
        break;
      default:
        break;
    }

    // Search filtering
    if (searchTerm) {
      filtered = filtered.filter(
        (wo) =>
          wo.basicInformation?.title
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          wo.workOrderNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (wo.contractorAssignment?.primaryContractor?.contractorName ||
           wo.contractorAssignment?.primaryContractor?.companyName)
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Status filtering
    if (filterStatus !== "all") {
      filtered = filtered.filter((wo) => wo.status === filterStatus);
    }

    // Priority filtering
    if (filterPriority !== "all") {
      filtered = filtered.filter(
        (wo) => wo.basicInfo?.priority === filterPriority
      );
    }

    return filtered;
  };

  const filteredWorkOrders = getFilteredWorkOrders();

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "active":
        return "info";
      case "completed":
        return "success";
      case "pending_approval":
      case "submitted":
        return "warning";
      case "cancelled":
      case "terminated":
        return "error";
      case "on_hold":
        return "warning";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Work Order Management</Typography>
          {user?.role !== "contractor_user" &&
            user?.role !== "contractor_admin" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateWorkOrder}
              >
                Create Work Order
              </Button>
            )}
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <WorkOrderIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{workOrders.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Work Orders
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ContractorIcon color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {
                        workOrders.filter(
                          (wo) =>
                            wo.status === "approved" ||
                            wo.status === "in_progress"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Orders
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TimelineIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {
                        workOrders.filter(
                          (wo) =>
                            wo.statusTracking?.currentStatus ===
                              "pending_approval" ||
                            wo.statusTracking?.currentStatus === "submitted"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending Approval
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {
                        workOrders.filter(
                          (wo) =>
                            wo.statusTracking?.currentStatus === "approved" ||
                            wo.statusTracking?.currentStatus === "completed"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <DeleteIcon color="error" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {
                        workOrders.filter(
                          (wo) =>
                            wo.statusTracking?.currentStatus === "cancelled" ||
                            wo.statusTracking?.currentStatus === "terminated"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cancelled
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  placeholder="Search work orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  label="Priority"
                >
                  {priorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" startIcon={<FilterIcon />}>
                    More Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Badge
                    badgeContent={
                      tab.value === 0
                        ? workOrders.length
                        : tab.value === 1
                        ? workOrders.filter(
                            (wo) =>
                              wo.statusTracking?.currentStatus === "active"
                          ).length
                        : tab.value === 2
                        ? workOrders.filter(
                            (wo) =>
                              wo.statusTracking?.currentStatus ===
                                "pending_approval" ||
                              wo.statusTracking?.currentStatus === "submitted"
                          ).length
                        : tab.value === 4
                        ? workOrders.filter(
                            (wo) =>
                              wo.statusTracking?.currentStatus ===
                                "cancelled" ||
                              wo.statusTracking?.currentStatus === "terminated"
                          ).length
                        : workOrders.filter(
                            (wo) =>
                              wo.statusTracking?.currentStatus === "approved" ||
                              wo.statusTracking?.currentStatus === "completed"
                          ).length
                    }
                    color="primary"
                  >
                    {tab.label}
                  </Badge>
                }
              />
            ))}
          </Tabs>
        </Card>

        {/* Work Orders Table */}
        <Card>
          <CardContent>
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Work Order</TableCell>
                    {!(
                      user?.role === "contractor_user" ||
                      user?.role === "contractor_admin"
                    ) && <TableCell>Contractor</TableCell>}
                    <TableCell>Location</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWorkOrders.map((workOrder) => (
                    <TableRow key={workOrder._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            {workOrder.basicInfo?.title?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {workOrder.basicInfo?.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {workOrder.workOrderNumber}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {!(
                        user?.role === "contractor_user" ||
                        user?.role === "contractor_admin"
                      ) && (
                        <TableCell>
                          <Typography variant="body2">
                            {workOrder.contractorAssignment?.primaryContractor
                              ?.contractorName || 
                             workOrder.contractorAssignment?.primaryContractor
                              ?.companyName || "N/A"}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2">
                          {workOrder.workLocation?.siteName || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workOrder.basicInformation?.priority || "N/A"}
                          color={getPriorityColor(
                            workOrder.basicInformation?.priority
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            workOrder.statusTracking?.currentStatus || "N/A"
                          }
                          color={getStatusColor(
                            workOrder.statusTracking?.currentStatus
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {workOrder.timeline?.plannedStartDate
                            ? dayjs(workOrder.timeline.plannedStartDate).format(
                                "DD/MM/YYYY"
                              )
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {workOrder.timeline?.plannedEndDate
                            ? dayjs(workOrder.timeline.plannedEndDate).format(
                                "DD/MM/YYYY"
                              )
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {workOrder.statusTracking?.progressPercentage ||
                            workOrder.statusTracking?.overallProgress ||
                            0}
                          %
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewWorkOrder(workOrder)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>

                          {/* Contractor-specific actions */}
                          {user?.role === "contractor_user" ||
                          user?.role === "contractor_admin" ? (
                            <>
                              <Tooltip title="Update Progress">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleUpdateProgress(workOrder)
                                  }
                                  color="primary"
                                >
                                  <ProgressIcon />
                                </IconButton>
                              </Tooltip>
                              {workOrder.statusTracking?.currentStatus ===
                                "active" && (
                                <Tooltip title="Submit for Approval">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleSubmitForApproval(workOrder)
                                    }
                                    color="success"
                                  >
                                    <SubmitIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              {/* Tenant Admin actions */}
                              <Tooltip title="View Progress">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewProgress(workOrder)}
                                  color="info"
                                >
                                  <ViewProgressIcon />
                                </IconButton>
                              </Tooltip>

                                {/* Approval/Rejection for submitted work orders */}
                                {(workOrder.statusTracking?.currentStatus ===
                                  "pending_approval" ||
                                  workOrder.statusTracking?.currentStatus ===
                                    "submitted") && (
                                <>
                                  <Tooltip title="Approve Work Order">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleApproveWorkOrder(workOrder)
                                      }
                                      color="success"
                                    >
                                      <ApproveIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject Work Order">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleRejectWorkOrder(workOrder)
                                      }
                                      color="error"
                                    >
                                      <RejectIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}

                              {/* Edit and Delete for approved work orders */}
                              {workOrder.statusTracking?.currentStatus ===
                                "approved" && (
                                <>
                                  <Tooltip title="Edit Work Order">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleEditWorkOrder(workOrder)
                                      }
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel Work Order">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleDeleteWorkOrder(workOrder._id)
                                      }
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredWorkOrders.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No work orders found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ||
                  filterStatus !== "all" ||
                  filterPriority !== "all"
                    ? "Try adjusting your search criteria"
                    : "Create your first work order to get started"}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Form Dialog */}
        <Dialog
          open={openForm}
          onClose={handleFormClose}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle>
            {selectedWorkOrder ? "Edit Work Order" : "Create New Work Order"}
          </DialogTitle>
          <DialogContent>
            <WorkOrderForm
              workOrder={selectedWorkOrder}
              onSave={handleFormSave}
              onCancel={handleFormClose}
            />
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Work Order Details</DialogTitle>
          <DialogContent>
            {selectedWorkOrder && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                      <Typography variant="body2">
                        <strong>Title:</strong>{" "}
                        {selectedWorkOrder.basicInformation?.title || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Work Order #:</strong>{" "}
                        {selectedWorkOrder.workOrderNumber || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong>{" "}
                        {selectedWorkOrder.basicInformation?.workOrderType ||
                          "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Priority:</strong>{" "}
                        {selectedWorkOrder.basicInformation?.priority || "N/A"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Timeline
                      </Typography>
                      <Typography variant="body2">
                        <strong>Start Date:</strong>{" "}
                        {selectedWorkOrder.timeline?.plannedStartDate
                          ? dayjs(
                              selectedWorkOrder.timeline.plannedStartDate
                            ).format("DD/MM/YYYY")
                          : "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>End Date:</strong>{" "}
                        {selectedWorkOrder.timeline?.plannedEndDate
                          ? dayjs(
                              selectedWorkOrder.timeline.plannedEndDate
                            ).format("DD/MM/YYYY")
                          : "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Duration:</strong>{" "}
                        {selectedWorkOrder.timeline?.estimatedDuration || "N/A"}{" "}
                        days
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenViewDialog(false);
                handleEditWorkOrder(selectedWorkOrder);
              }}
            >
              Edit Work Order
            </Button>
          </DialogActions>
        </Dialog>
        {/* Progress Update Dialog */}
        <Dialog
          open={openProgressDialog}
          onClose={() => setOpenProgressDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Update Work Order Progress</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Work Order: {selectedWorkOrder?.basicInformation?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Progress:{" "}
                {selectedWorkOrder?.statusTracking?.progressPercentage ||
                  selectedWorkOrder?.statusTracking?.overallProgress ||
                  0}
                %
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Progress Percentage
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{ mb: 1, height: 8, borderRadius: 4 }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Slider
                    value={progressValue}
                    onChange={(e, newValue) => setProgressValue(newValue)}
                    min={0}
                    max={100}
                    step={5}
                    sx={{ flexGrow: 1 }}
                  />
                  <Typography variant="body2" sx={{ minWidth: 40 }}>
                    {progressValue}%
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comments"
                value={progressComments}
                onChange={(e) => setProgressComments(e.target.value)}
                placeholder="Add any comments about the progress..."
                sx={{ mt: 3 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenProgressDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitProgress} variant="contained">
              Update Progress
            </Button>
          </DialogActions>
        </Dialog>

        {/* Submit for Approval Dialog */}
        <Dialog
          open={openSubmitDialog}
          onClose={() => setOpenSubmitDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Submit Work Order for Approval</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Work Order: {selectedWorkOrder?.basicInformation?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progress:{" "}
                {selectedWorkOrder?.statusTracking?.progressPercentage ||
                  selectedWorkOrder?.statusTracking?.overallProgress ||
                  0}
                %
              </Typography>

              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                Please ensure all work is completed before submitting for
                approval.
              </Alert>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Completion Comments"
                value={submitComments}
                onChange={(e) => setSubmitComments(e.target.value)}
                placeholder="Describe the completed work and any important details..."
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSubmitDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitApproval}
              variant="contained"
              color="success"
              disabled={!submitComments.trim()}
            >
              Submit for Approval
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog
          open={openApprovalDialog}
          onClose={() => setOpenApprovalDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Approve Work Order</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Work Order: {selectedWorkOrder?.basicInformation?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Contractor:{" "}
                {selectedWorkOrder?.contractorAssignment?.primaryContractor
                  ?.contractorName || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progress:{" "}
                {selectedWorkOrder?.statusTracking?.progressPercentage || 0}%
              </Typography>

              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                This work order will be marked as approved and completed.
              </Alert>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Approval Comments"
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                placeholder="Add any comments about the approval..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenApprovalDialog(false)}>Cancel</Button>
            <Button
              onClick={handleApproveSubmission}
              variant="contained"
              color="success"
            >
              Approve Work Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rejection Dialog */}
        <Dialog
          open={openRejectionDialog}
          onClose={() => setOpenRejectionDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Work Order</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Work Order: {selectedWorkOrder?.basicInformation?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Contractor:{" "}
                {selectedWorkOrder?.contractorAssignment?.primaryContractor
                  ?.contractorName || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progress:{" "}
                {selectedWorkOrder?.statusTracking?.progressPercentage || 0}%
              </Typography>

              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                This work order will be rejected and sent back to the contractor
                for revision.
              </Alert>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Rejection Reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please specify the reason for rejection and what needs to be corrected..."
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRejectionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRejection}
              variant="contained"
              color="error"
              disabled={!rejectionReason.trim()}
            >
              Reject Work Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Progress View Dialog */}
        <Dialog
          open={openProgressViewDialog}
          onClose={() => setOpenProgressViewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Work Order Progress Details</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedWorkOrder?.basicInformation?.title ||
                  "Work Order Details"}
              </Typography>

              <Grid container spacing={3}>
                {/* Basic Info */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Basic Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Work Order #:{" "}
                      {selectedWorkOrder?.workOrderNumber || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type:{" "}
                      {selectedWorkOrder?.basicInformation?.workOrderType ||
                        "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Priority:{" "}
                      {selectedWorkOrder?.basicInformation?.priority || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status:{" "}
                      {selectedWorkOrder?.statusTracking?.currentStatus ||
                        "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Progress Info */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Progress Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Overall Progress:{" "}
                      {selectedWorkOrder?.statusTracking?.progressPercentage ||
                        0}
                      %
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        selectedWorkOrder?.statusTracking?.progressPercentage ||
                        0
                      }
                      sx={{ mb: 2, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Current Status:{" "}
                      {selectedWorkOrder?.statusTracking?.currentStatus ||
                        "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated:{" "}
                      {selectedWorkOrder?.updatedAt
                        ? new Date(
                            selectedWorkOrder.updatedAt
                          ).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Contractor Info */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Contractor Assignment
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Contractor:{" "}
                      {selectedWorkOrder?.contractorAssignment
                        ?.primaryContractor?.contractorName || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact Person:{" "}
                      {selectedWorkOrder?.contractorAssignment
                        ?.primaryContractor?.contactPerson || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact Number:{" "}
                      {selectedWorkOrder?.contractorAssignment
                        ?.primaryContractor?.contactNumber || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email:{" "}
                      {selectedWorkOrder?.contractorAssignment
                        ?.primaryContractor?.emailAddress || "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Timeline Info */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Timeline
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Start Date:{" "}
                      {selectedWorkOrder?.timeline?.plannedStartDate
                        ? new Date(
                            selectedWorkOrder.timeline.plannedStartDate
                          ).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      End Date:{" "}
                      {selectedWorkOrder?.timeline?.plannedEndDate
                        ? new Date(
                            selectedWorkOrder.timeline.plannedEndDate
                          ).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration:{" "}
                      {selectedWorkOrder?.timeline?.estimatedDuration || "N/A"}{" "}
                      days
                    </Typography>
                  </Box>
                </Grid>

                {/* Progress Comments */}
                {selectedWorkOrder?.progressComments && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      Progress Comments
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <Typography variant="body2">
                        {selectedWorkOrder.progressComments}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenProgressViewDialog(false)}>
              Close
            </Button>
            {(user?.role === "admin" || user?.role === "superadmin") &&
              selectedWorkOrder?.statusTracking?.currentStatus ===
                "completed" && (
                <>
                  <Button
                    onClick={() => handleApproveWorkOrder(selectedWorkOrder)}
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                  >
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleRejectWorkOrder(selectedWorkOrder)}
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                  >
                    Reject
                  </Button>
                </>
              )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default WorkOrders;
